import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash, CheckCircle, Lightning } from "@phosphor-icons/react";
import { invoicesApi, customersApi } from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState({ customer_id: "", invoice_number: "", amount: "", currency: "INR", due_date: "", description: "" });
  const navigate = useNavigate();

  const load = async () => {
    const [inv, cust] = await Promise.all([invoicesApi.list(), customersApi.list()]);
    setInvoices(inv); setCustomers(cust);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await invoicesApi.create({ ...form, amount: parseFloat(form.amount) });
      toast.success("Invoice created");
      setOpen(false);
      setForm({ customer_id: "", invoice_number: "", amount: "", currency: "INR", due_date: "", description: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create invoice"); }
  };

  const markPaid = async (id) => {
    await invoicesApi.update(id, { status: "paid" });
    toast.success("Marked as paid");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await invoicesApi.remove(id);
    toast.success("Invoice deleted");
    load();
  };

  const filtered = filter === "all" ? invoices : invoices.filter(i => i.status === filter);
  const statusColor = {
    paid: "bg-success/15 text-success border-success/30",
    overdue: "bg-destructive/15 text-destructive border-destructive/30",
    pending: "bg-muted text-foreground border-border",
    cancelled: "bg-muted text-muted-foreground border-border",
  };

  return (
    <AppLayout>
      <Topbar
        title="Invoices"
        subtitle={`${filtered.length} invoices`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-button" className="bg-primary text-primary-foreground">
                <Plus className="h-4 w-4 mr-1.5" /> Add Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="invoice-dialog">
              <DialogHeader><DialogTitle className="font-display text-2xl">New Invoice</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div className="col-span-2">
                  <Label>Customer *</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger data-testid="invoice-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.company || c.phone}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Invoice # *</Label><Input data-testid="invoice-number-input" value={form.invoice_number} onChange={e => setForm({ ...form, invoice_number: e.target.value })} /></div>
                <div><Label>Due Date *</Label><Input data-testid="invoice-due-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} /></div>
                <div><Label>Amount *</Label><Input data-testid="invoice-amount-input" type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
                <div>
                  <Label>Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR ₹</SelectItem>
                      <SelectItem value="USD">USD $</SelectItem>
                      <SelectItem value="EUR">EUR €</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-invoice-button" onClick={submit} className="bg-primary text-primary-foreground">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="px-6 lg:px-10 py-8">
        <div className="flex items-center gap-2 mb-5">
          {["all", "overdue", "pending", "paid"].map(s => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card text-foreground border-border hover:bg-muted"
              }`}
            >
              {s.toUpperCase()}
            </button>
          ))}
        </div>
        <div className="border border-border bg-card rounded-md overflow-hidden" data-testid="invoices-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Overdue</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No invoices in this view.</TableCell></TableRow>
              )}
              {filtered.map((i, idx) => (
                <TableRow key={i.id} className="stagger-in" style={{ animationDelay: `${idx * 25}ms` }} data-testid={`invoice-row-${i.id}`}>
                  <TableCell className="font-mono text-sm">{i.invoice_number}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{i.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{i.customer_company || i.customer_phone}</div>
                  </TableCell>
                  <TableCell className="font-mono tabular-nums font-semibold">{formatCurrency(i.amount, i.currency)}</TableCell>
                  <TableCell className="text-sm">{formatDate(i.due_date)}</TableCell>
                  <TableCell>
                    {i.days_overdue > 0 ? (
                      <span className="font-mono text-sm text-destructive">{i.days_overdue}d</span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColor[i.status]} border font-mono text-[10px] uppercase`}>{i.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {i.status !== "paid" && (
                      <>
                        <Button
                          variant="ghost" size="sm"
                          data-testid={`agent-invoice-${i.id}`}
                          onClick={() => navigate(`/agent?invoice=${i.id}`)}
                          title="Open AI Agent"
                        >
                          <Lightning className="h-4 w-4 text-primary" weight="fill" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => markPaid(i.id)} data-testid={`mark-paid-${i.id}`} title="Mark as paid">
                          <CheckCircle className="h-4 w-4 text-success" />
                        </Button>
                      </>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => remove(i.id)} data-testid={`delete-invoice-${i.id}`}>
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  );
}
